import React from 'react';
import { getIn } from 'formik';
import MuiTextField from '@mui/material/TextField';
import MuiSwitch from '@mui/material/Switch';
import MuiRadioGroup from '@mui/material/RadioGroup';
import MuiSelect from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';

export function fieldToTextField({
  disabled,
  field: { onBlur: fieldOnBlur, ...field },
  form: { isSubmitting, touched, errors },
  onBlur,
  helperText,
  ...props
}) {
  const fieldError = getIn(errors, field.name);
  const showError = getIn(touched, field.name) && Boolean(fieldError);

  return {
    error: showError,
    helperText: showError ? fieldError : helperText,
    disabled: disabled === undefined ? isSubmitting : disabled,
    onBlur:
      onBlur ||
      function handleBlur(event) {
        fieldOnBlur(event || field.name);
      },
    ...field,
    ...props,
  };
}

export function TextField({ children, ...props }) {
  return <MuiTextField {...fieldToTextField(props)}>{children}</MuiTextField>;
}

TextField.displayName = 'FormikMaterialUITextField';

export function fieldToSwitch({
  disabled,
  field: { onBlur: fieldOnBlur, ...field },
  form: { isSubmitting },
  onBlur,
  ...props
}) {
  return {
    disabled: disabled === undefined ? isSubmitting : disabled,
    onBlur:
      onBlur ||
      function handleBlur(event) {
        fieldOnBlur(event || field.name);
      },
    ...field,
    ...props,
  };
}

export function Switch(props) {
  return <MuiSwitch {...fieldToSwitch(props)} />;
}

Switch.displayName = 'FormikMaterialUISwitch';

export function fieldToRadioGroup({
  field: { onBlur: fieldOnBlur, ...field },
  onBlur,
  ...props
}) {
  return {
    onBlur:
      onBlur ||
      function handleBlur(event) {
        fieldOnBlur(event || field.name);
      },
    ...field,
    ...props,
  };
}

export function RadioGroup(props) {
  return <MuiRadioGroup {...fieldToRadioGroup(props)} />;
}

RadioGroup.displayName = 'FormikMaterialUIRadioGroup';

export function fieldToSelect({
  disabled,
  field: { onBlur: _onBlur, onChange: fieldOnChange, ...field },
  form: { isSubmitting, touched, errors, setFieldTouched, setFieldValue },
  onClose,
  ...props
}) {
  const fieldError = getIn(errors, field.name);
  const showError = getIn(touched, field.name) && Boolean(fieldError);

  return {
    disabled: disabled === undefined ? isSubmitting : disabled,
    error: showError,
    formError: showError ? fieldError : undefined,
    onBlur: function handleBlur() {},
    onChange: fieldOnChange || function handleChange() {},
    onClose:
      onClose ||
      function handleClose(event) {
        const dataset = event && event.target && event.target.dataset;

        if (dataset && dataset.value) {
          setFieldValue(field.name, dataset.value, false);
        }

        setFieldTouched(field.name, true, true);
      },
    ...field,
    ...props,
  };
}

export function Select({ formControl, inputLabel, formHelperText, ...props }) {
  const { error, formError, disabled, ...selectFieldProps } =
    fieldToSelect(props);
  const helperText = formHelperText || {};
  const { children: helperTextChildren, ...formHelperTextProps } = helperText;
  const shouldDisplayHelperText = error || helperTextChildren;

  return (
    <FormControl disabled={disabled} error={error} {...formControl}>
      <InputLabel id={selectFieldProps.labelId} {...inputLabel}>
        {selectFieldProps.label}
      </InputLabel>
      <MuiSelect {...selectFieldProps} />
      {shouldDisplayHelperText && (
        <FormHelperText {...formHelperTextProps}>
          {error ? formError : helperTextChildren}
        </FormHelperText>
      )}
    </FormControl>
  );
}

Select.displayName = 'FormikMaterialUISelect';
